import { useQuery } from '@tanstack/react-query';

/**
 * Query key factory for bulk upload data
 */
export const bulkUploadDataQueryKeys = {
    all: ['bulkUpload', 'data'],
    byKey: (key, params = {}) => ['bulkUpload', 'data', key, params],
};

/**
 * Query hook for fetching bulk upload data.
 *
 * @param {Object} options
 * @param {Object} options.api - API object with fetch function
 * @param {Object} options.params - Additional params to pass to the API
 * @param {string|null} options.uploadKey - The bulk upload key (query is disabled if null)
 * @returns {import('@tanstack/react-query').UseQueryResult}
 */
function useFetchBulkUploadData({ api, params, uploadKey }) {
    return useQuery({
        enabled: !!uploadKey,
        gcTime: 30 * 60_000, // Keep in cache for 30 minutes
        queryFn: async ({ signal }) => {
            const response = await api.fetch(uploadKey, params, signal);
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to fetch bulk upload data');
            }
            const rows = response.data?.rows?.sort((a, b) => a.rowIndex - b.rowIndex) ?? [];
            return { ...response.data, rows };
        },
        queryKey: bulkUploadDataQueryKeys.byKey(uploadKey, params),
        staleTime: Infinity,
    });
}

export { useFetchBulkUploadData };
